/** @jsx jsx */

import React, {
  Fragment as _,
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
  memo,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import placeholder from 'assets/addImg@3x.svg';
import getBlockById from 'services/apiHelpers/getBlockById';
import {
  EditWrapper0,
  EditWrapper1,
  EditWrapper2,
  EditWrapper3,
} from './EditWrappers';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import checkImgSrcIsGif from 'services/checkImgSrcIsGif';
import Circle from './Circle';
import getStore from 'services/getStore';
import { ImageZoom } from './ImageZoom';
import { getCache } from 'services/imageCache';

const store = getStore();
const cachedSrcs = {};

const cachedSrc = (id, src = null) => {
  if (src) {
    cachedSrcs[id] = src;
    return;
  }
  return cachedSrcs[id];
};

const getCalculatedHeight = (
  imageWidth,
  imageHeight,
  maxHeight = 50,
  containerWidth
) => {
  const ratio = imageWidth / imageHeight;
  const calculatedMaxHeight = window.innerHeight * (maxHeight / 100);
  let calculatedHeight = containerWidth / ratio;

  if (calculatedHeight > calculatedMaxHeight) {
    calculatedHeight = calculatedMaxHeight;
  }

  return calculatedHeight;
};

export const ImageContext = createContext<{
  editWrapperType?: 0 | 1 | 2 | 3;
  editWrapperSize?: string;
  editWrapperUseIcon?: boolean;
  placeholder?: string;
  canUpdateContainerHeight?: boolean;
  padding?: number;
}>({});

const Image = props => {
  const imageContext = useContext(ImageContext) as any;

  let {
    id,
    mediaParentId = null,
    editWrapperType = imageContext?.editWrapperType ??
      props?.editWrapperType ??
      0,
    editWrapperSize = imageContext?.editWrapperSize ||
      props?.editWrapperSize ||
      '50%',
    editWrapperUseIcon = imageContext?.editWrapperUseIcon ??
      props?.editWrapperUseIcon ??
      true,
    padding = imageContext?.padding ?? props?.padding ?? 0,
    usePlaceholderValue = false,
    maxHeight = 50,

    //
    editWrapperPadding = 0,
  } = props;

  const block = getBlockById(id);

  const focusedNodeId = useSelector((state: any) => state.focusedNodeId);

  let {
    src = imageContext.placeholder || props.placeholder || placeholder,
    borderStyle = 'rectangle',
    fit = 'contain',
    fixedHeight = false,
    fixedHeightValue = 350,
    imgSize = 0,
    defaultHeight = true,
    imgContainerHeight = 0,
    imgMagnification = 100,
    description,
    layout = '',
    width = 800,
    height = 600,
    linkData,
    imageZoom = false,
    imageZoomIconColor,
    imageZoomBackgroundColor = '#fff',

    //
  } = (block && block.props) || ({} as any);

  const canUpdateContainerHeight =
    imageContext.canUpdateContainerHeight ??
    props.canUpdateContainerHeight ??
    true;

  const pageBackgroundColor = useRef<string>('#fff');
  const aHiliteColor = useRef<string>('#060709');

  src = useMemo(() => {
    if (window.dev()) {
      return src;
    }

    return checkImgSrcIsGif(src);
  }, [src]);

  let [imgLoadedState, setLoadedState] = useState(0);
  let [realSrc, setRealSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    const cacheAvailable = 'caches' in self;

    if (!cacheAvailable) {
      setLoadedState(2);
      return;
    }

    const cache = getCache();

    cache.then(c => {
      c.match(src)
        .then(response => {
          if (response) {
            if (!response.ok) {
              throw Error('invalid response');
            }
            // turn it into a usable src value
            // console.log('response is in the cache');
            return response.blob().then(blob => {
              // console.log('response is now a blob');

              const objectURL = URL.createObjectURL(blob);
              setRealSrc(objectURL);
              setLoadedState(2);
            });
          } else {
            // put in the cache for the future
            // console.log('not in cache');
            return fetch(src)
              .then(resp => {
                if (!resp.ok) {
                  throw Error('invalid response');
                }

                const r2 = resp.clone();
                c.put(src, resp);
                return r2;
              })
              .then(resp => resp.blob())
              .then(blob => {
                // throw new Error('forced error');
                const objectURL = URL.createObjectURL(blob);
                setRealSrc(objectURL);
                setLoadedState(2);
              });
          }
        })
        .catch(e => {
          console.log(e);
          c.delete(src);
          setLoadedState(2);
        });
    });

    setTimeout(() => {
      if (imgLoadedState !== 2) return;
      setLoadedState(1);
    }, 300);

    return () => {
      // console.log('ive been unmounted');
    };
  }, [src]);

  const selected = focusedNodeId === id;

  const image: any = useRef(null);
  const hasResized = useRef(false);

  const borderRadius = 50;

  useLayoutEffect(() => {
    if (!usePlaceholderValue || !image.current) {
      return;
    }

    const targetHeight = getCalculatedHeight(
      width,
      height,
      maxHeight,
      image.current.getBoundingClientRect().width
    );

    image.current.style.height = `${targetHeight}px`;

    const onResize = () => {
      if (!hasResized.current) {
        image.current.style.height = '';
        //
        window.removeEventListener('resize', onResize);
        hasResized.current = true;
      }
    };

    if (!hasResized.current && imgLoadedState === 2) {
      onResize();
      window.addEventListener('resize', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [imgLoadedState]);

  useEffect(() => {
    if (!image.current) {
      return;
    }

    const section = image.current.closest('section') as HTMLElement;

    if (!section) {
      return;
    }

    pageBackgroundColor.current = window.getComputedStyle(
      section
    ).backgroundColor;

    if (pageBackgroundColor.current === 'rgba(0, 0, 0, 0)') {
      pageBackgroundColor.current = '#fff';
    }

    const button = section?.querySelector<HTMLButtonElement>(
      '[data-as-exit-btn]'
    );

    if (button) {
      // get find button background color
      const buttonStyles = window.getComputedStyle(button);
      aHiliteColor.current = buttonStyles.backgroundColor;
    }
  }, []);

  if (realSrc) {
    src = realSrc;
  }

  const EditWrapper = window.dev()
    ? useMemo(
        () => ({ children }) =>
          ({
            '0': (
              <EditWrapper0
                id={id}
                mediaParentId={mediaParentId}
                padding={padding}
                selected={selected}
              >
                {children}
              </EditWrapper0>
            ),
            '1': (
              <EditWrapper1
                id={id}
                mediaParentId={mediaParentId}
                editWrapperUseIcon={editWrapperUseIcon}
                editWrapperSize={editWrapperSize}
                selected={selected}
              >
                {children}
              </EditWrapper1>
            ),
            '2': (
              <EditWrapper2
                id={id}
                mediaParentId={mediaParentId}
                selected={selected}
              >
                {children}
              </EditWrapper2>
            ),
            '3': (
              <EditWrapper3
                id={id}
                mediaParentId={mediaParentId}
                padding={padding}
                editWrapperPadding={editWrapperPadding}
                selected={selected}
              >
                {children}
              </EditWrapper3>
            ),
          }[editWrapperType]),
        [selected]
      )
    : ({ children }) => <_>{children}</_>;

  // parsed is not the correct term is it. but. . .
  let parsedImgSize;

  if (imgSize) {
    parsedImgSize = window.innerHeight / 2 + imgSize;
  }

  if (props.fullHeight) {
    parsedImgSize = window.innerHeight - 70 * 2;
  }

  const noDescription = '';

  if (borderStyle === 'circle') {
    return (
      <EditWrapper key={src}>
        <Circle
          id={id}
          src={src}
          alt={description || noDescription}
          fit={fit}
          cachedSrc={cachedSrc}
          linkData={linkData}
        />
      </EditWrapper>
    );
  }

  return (
    <_>
      <EditWrapper>
        <div
          ref={image}
          id={`chameleon-image-${id}`}
          className="chameleon-image"
          css={css`
            position: relative;
            width: 100%;

            img:not(.zoom-image img) {
              width: 100%;
              max-height: ${50 + imgContainerHeight}vh;
              //

              ${imgContainerHeight >= 0
                ? `padding: ${imgContainerHeight / 2}px 0;`
                : `
                  width: calc(100% - ${Math.abs(imgContainerHeight)}px);
                  padding: 0;
                  `}

              opacity: 0;

              @media (max-width: 768px) {
                max-height: unset;
              }
            }

            ${imgMagnification > 100 ? 'overflow: hidden;' : ''}

            ${window.dev &&
              !window.dev() &&
              linkData &&
              linkData.url &&
              `
          a {
            display: block;
            transition: box-shadow 0.1s;

            ${borderStyle === 'roundedRectangle' &&
              `border-radius: ${borderRadius}px;`}
            
            ${window.dev && window.dev() && 'pointer-events: none;'}
          }

          a:hover {
            box-shadow: 0 0 0 2px ${pageBackgroundColor.current ||
              '#fff'}, 0 0 0 3px ${aHiliteColor.current} !important;
          }

          a:focus {
            box-shadow: 0 0 0 1px ${aHiliteColor.current}, 0 0 0 3px ${
                pageBackgroundColor.current
              }, 0 0 0 4px ${aHiliteColor.current} !important;
          }
        
          `}
          `}
          data-can-update-container-height={canUpdateContainerHeight}
        >
          {imgLoadedState !== 2 ? (
            <LoadingSpinner />
          ) : (
            <div
              // id={id}
              css={css`
						width: 100%;
          
            
            transform: scale(${imgMagnification * 0.01});

						${borderStyle === 'roundedRectangle' &&
              fit === 'contain' &&
              'min-height: auto;'}
							background: url('${src}') no-repeat;
							background-size: ${fit};
							background-position: center;
            ${borderStyle === 'roundedRectangle' &&
              `border-radius: ${borderRadius}px;`}

					`}
            >
              {linkData ? (
                <a
                  href={linkData.url}
                  title={linkData.title}
                  target={linkData.target}
                >
                  <img
                    id={`img-${id}`}
                    src={src}
                    alt={description || noDescription}
                    style={{ pointerEvents: 'none' }}
                  />
                </a>
              ) : (
                <img
                  id={`img-${id}`}
                  src={src}
                  alt={description || noDescription}
                />
              )}
            </div>
          )}

          {imageZoom && (
            <ImageZoom
              src={src}
              description={description}
              noDescription={noDescription}
              imageZoomIconColor={imageZoomIconColor}
              imageZoomBackgroundColor={imageZoomBackgroundColor}
            />
          )}
        </div>
      </EditWrapper>
    </_>
  );
};

// moved the focusedNodeId into the component > react-redux useSelector hook
// this makes sure our component updates when src changed
export default props => {
  return <Image key={props.src} {...props} />;
};
